import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslate } from "@refinedev/core";

import { AgreementIntegrationConfig } from "@modules/erpIntegrations/types";
import { getErpErrorMessage } from "@modules/erpIntegrations/utils";
import {
  PlaygroundCatalog,
  PlaygroundField,
  PlaygroundResult,
  getPlaygroundCatalog,
  runPlayground,
} from "@modules/erpIntegrations/services/playground";

import { CopyTextButton } from "../CopyTextButton";

interface PlaygroundDialogProps {
  config: AgreementIntegrationConfig | null;
  onClose: () => void;
  onDone: () => void;
}

const CREDENTIAL_FALLBACKS: Record<string, string> = {
  username: "name",
  client_secret: "token",
  name: "username",
  token: "client_secret",
};

function credentialText(credentials: Record<string, unknown>, key: string) {
  const direct = credentials[key];
  if (direct !== undefined && direct !== null && String(direct) !== "") return String(direct);
  const fallbackKey = CREDENTIAL_FALLBACKS[key];
  const fallback = fallbackKey ? credentials[fallbackKey] : undefined;
  if (fallback === undefined || fallback === null || String(fallback) === "") return "";
  return String(fallback);
}

function credentialsForForm(catalog: PlaygroundCatalog) {
  const raw = catalog.credentials ?? {};
  const schema = catalog.credential_schema ?? [];
  if (schema.length === 0) {
    return Object.fromEntries(Object.entries(raw).map(([key, value]) => [key, String(value ?? "")]));
  }
  return Object.fromEntries(schema.map((field) => [field.key, credentialText(raw, field.key)]));
}

function fieldValues(
  fields: PlaygroundField[],
  credentials: Record<string, unknown>,
  accessToken: string
) {
  return Object.fromEntries(
    fields.map((field) => {
      if (field.from_access_token) return [field.key, accessToken];
      const own = field.value ?? "";
      if (own !== "") return [field.key, own];
      return [field.key, credentialText(credentials, field.key)];
    })
  );
}

function asText(value: unknown) {
  if (value === undefined || value === null) return "";
  return typeof value === "string" ? value : JSON.stringify(value, null, 2);
}

export function PlaygroundDialog({ config, onClose, onDone }: PlaygroundDialogProps) {
  const translate = useTranslate();
  const open = config !== null;
  const catalogQuery = useQuery({
    queryKey: ["integration-playground", config?.integration, config?.agreement],
    queryFn: () => getPlaygroundCatalog(config!.integration, config!.agreement),
    enabled: open,
  });

  const [endpointId, setEndpointId] = useState("");
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [queryValues, setQueryValues] = useState<Record<string, string>>({});
  const [headerValues, setHeaderValues] = useState<Record<string, string>>({});
  const [bodyValues, setBodyValues] = useState<Record<string, string>>({});
  const [result, setResult] = useState<PlaygroundResult | null>(null);
  const [error, setError] = useState("");

  const catalog = catalogQuery.data;
  const endpoint = catalog?.endpoints.find((item) => item.id === endpointId) ?? catalog?.endpoints[0];

  const accessToken = (config?.access_token ?? "").trim();

  useEffect(() => {
    if (!catalog) return;
    const first = catalog.endpoints[0];
    const filled = credentialsForForm(catalog);
    setEndpointId(first?.id ?? "");
    setCredentials(filled);
    setQueryValues(fieldValues(first?.query ?? [], filled, accessToken));
    setHeaderValues(fieldValues(first?.headers ?? [], filled, accessToken));
    setBodyValues(fieldValues(first?.body ?? [], filled, accessToken));
    setResult(null);
    setError("");
  }, [catalog, accessToken]);

  const selectEndpoint = (nextId: string) => {
    const next = catalog?.endpoints.find((item) => item.id === nextId);
    setEndpointId(nextId);
    setQueryValues(fieldValues(next?.query ?? [], credentials, accessToken));
    setHeaderValues(fieldValues(next?.headers ?? [], credentials, accessToken));
    setBodyValues(fieldValues(next?.body ?? [], credentials, accessToken));
    setResult(null);
  };

  const run = useMutation({
    mutationFn: () => {
      if (!config || !endpoint) throw new Error(translate("erp_integrations.playground.missing_endpoint"));
      return runPlayground({
        integration: config.integration,
        agreement: config.agreement,
        endpoint_id: endpoint.id,
        credentials,
        query: queryValues,
        headers: headerValues,
        body: endpoint.body.length > 0 ? bodyValues : null,
      });
    },
    onSuccess: (data) => {
      setResult(data);
      setError("");
      onDone();
    },
    onError: (err) => {
      setResult(null);
      setError(getErpErrorMessage(err));
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{translate("erp_integrations.playground.title")}</DialogTitle>
      <DialogContent>
        {catalogQuery.isError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {getErpErrorMessage(catalogQuery.error)}
          </Typography>
        )}
        {catalog && endpoint && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              label={translate("erp_integrations.playground.endpoint")}
              value={endpoint.id}
              onChange={(event) => selectEndpoint(event.target.value)}
              fullWidth
            >
              {catalog.endpoints.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.method} {item.label}
                </MenuItem>
              ))}
            </TextField>

            <FieldGroup
              title={translate("v1/agreement_integration_config_admin.fields.credentials")}
              fields={catalog.credential_schema.map((field) => ({
                key: field.key,
                label: field.label,
                type: field.type,
              }))}
              values={credentials}
              onChange={setCredentials}
            />
            <FieldGroup
              title={translate("erp_integrations.endpoints.query")}
              fields={endpoint.query}
              values={queryValues}
              onChange={setQueryValues}
            />
            <FieldGroup
              title={translate("erp_integrations.endpoints.headers")}
              fields={endpoint.headers}
              values={headerValues}
              onChange={setHeaderValues}
            />
            <FieldGroup
              title={translate("erp_integrations.endpoints.body")}
              fields={endpoint.body}
              values={bodyValues}
              onChange={setBodyValues}
            />

            {error && <Typography color="error">{error}</Typography>}
            {result && <PlaygroundOutput result={result} />}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button type="button" onClick={onClose}>
          {translate("buttons.cancel")}
        </Button>
        <Button
          type="button"
          variant="contained"
          disabled={!endpoint || run.isPending || catalogQuery.isLoading}
          onClick={() => run.mutate()}
        >
          {translate("erp_integrations.playground.run")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function FieldGroup({
  title,
  fields,
  values,
  onChange,
}: {
  title: string;
  fields: Array<PlaygroundField & { type?: string }>;
  values: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}) {
  const translate = useTranslate();
  if (fields.length === 0) return null;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Stack spacing={1}>
        {fields.map((field) => (
          <TextField
            key={field.key}
            label={field.label || field.key}
            type={field.type === "password" ? "password" : "text"}
            value={values[field.key] ?? ""}
            onChange={(event) => onChange({ ...values, [field.key]: event.target.value })}
            required={Boolean(field.required) && !field.from_access_token}
            helperText={
              field.from_access_token && !(values[field.key] ?? "")
                ? translate("erp_integrations.playground.access_token_hint")
                : undefined
            }
            fullWidth
            slotProps={{
              input: field.prefix
                ? { startAdornment: <Box sx={{ mr: 1, color: "text.secondary" }}>{field.prefix}</Box> }
                : undefined,
            }}
          />
        ))}
      </Stack>
    </Box>
  );
}

function PlaygroundOutput({ result }: { result: PlaygroundResult }) {
  const translate = useTranslate();

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">
        {result.status_code} · {result.elapsed_ms} ms
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
        {result.url}
      </Typography>
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2">{translate("erp_integrations.playground.curl")}</Typography>
          <CopyTextButton text={result.curl} />
        </Stack>
        <Box component="pre" sx={{ m: 0, p: 1, maxHeight: 160, overflow: "auto", bgcolor: "action.hover", whiteSpace: "pre-wrap" }}>
          {result.curl}
        </Box>
      </Box>
      <Box>
        <Typography variant="subtitle2">{translate("erp_integrations.playground.body")}</Typography>
        <Box component="pre" sx={{ m: 0, p: 1, maxHeight: 220, overflow: "auto", bgcolor: "action.hover", whiteSpace: "pre-wrap" }}>
          {asText(result.body)}
        </Box>
      </Box>
      <Box>
        <Typography variant="subtitle2">{translate("erp_integrations.playground.body_text")}</Typography>
        <Box component="pre" sx={{ m: 0, p: 1, maxHeight: 160, overflow: "auto", bgcolor: "action.hover", whiteSpace: "pre-wrap" }}>
          {result.body_text}
        </Box>
      </Box>
    </Stack>
  );
}
